import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  Experiment,
  ExperimentClient,
  ExperimentUser,
  Variants,
} from '@amplitude/experiment-react-native-client';

import { clearMinimalCartExperimentExposureSessions } from 'experiments/minimal-cart-exposure-sessions';
import { EventTypes, logEvent } from 'state/amplitude';
import { appVersion } from 'utils/app-version-info';
import { LogLevel } from 'utils/datadog/types';
import { getDeviceIdAsync } from 'utils/device-id';
import { region as buildRegion, getApiKey, minDevLogLevel } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import logger from 'utils/logger';
import noop from 'utils/noop';
import { TraceName, startTrace } from 'utils/performance/trace';
import { useMemoAll } from 'utils/use-memo-all';

import { RunningExperiments } from './running-experiments';
import { ExposureOptions, IExperimentsContext, VariantOptions } from './types';

export { RunningExperiments } from './running-experiments';

const Context = React.createContext<IExperimentsContext>({
  isLoading: false,
  getVariantValue: (_flag: string | RunningExperiments) => undefined,
  logExposure: (_flag: string | RunningExperiments, _options?: ExposureOptions) => void 0,
  handleLogin: noop,
  handleLogout: noop,
  allVariants: {} as Variants,
  variant: (_flag: RunningExperiments, _expectedVariant: string, _options?: VariantOptions) =>
    false,
});

export const useExperiments = () => useContext(Context);

export const experiments = Experiment.initializeWithAmplitudeAnalytics(
  getApiKey({ key: 'amplitudeExperiments' }),
  {
    debug: __DEV__ && minDevLogLevel() <= LogLevel.debug,
    automaticExposureTracking: false, // we track this manually for many experiments
    automaticFetchOnAmplitudeIdentityChange: true,
  }
);

const initialUserId = syncStorage.getString(SyncStorageKey.USER_ID);
let lastFetchedUserId = initialUserId;

export const experimentsCacheReadyPromise =
  typeof experiments.cacheReady === 'function' ? experiments.cacheReady() : Promise.resolve();
const experimentsCacheSettledPromise = experimentsCacheReadyPromise.catch(error => {
  logger.error({ error, message: 'Amplitude experiments cache init failure' });
});
export const experimentsFetchPromise = experimentsCacheSettledPromise.then(() =>
  fetchExperimentsForUser(initialUserId)
);

async function fetchExperimentsForUser(userId?: string): Promise<ExperimentClient> {
  const trace = startTrace(TraceName.APP_START_INIT_EXPERIMENTS);
  const deviceId = await getDeviceIdAsync();
  const user: ExperimentUser = {
    country: buildRegion,
    device_id: deviceId.toLowerCase(),
    region: buildRegion,
    user_properties: {
      region: buildRegion,
    },
    version: appVersion,
  };

  if (userId) {
    user.user_id = userId;
  }

  const result = await experiments.fetch(user);

  trace?.stop();

  return result;
}

export const ExperimentsProvider = ({ children }: { children: React.ReactNode }) => {
  const flagExposuresHandled = useRef(new Map<string, boolean>());
  const [isLoading, setIsLoading] = useState(true);
  const [variants, setVariants] = useState({} as Variants);

  const updateVariants = useCallback((newVariants: Variants) => {
    setVariants(prevVariants => {
      // see if anything actually changed we care about key or value (we don't care about the metadata field etc. we don't use it)
      const keys = new Set([...Object.keys(newVariants), ...Object.keys(prevVariants)]);
      for (const key of keys) {
        const a = newVariants[key];
        const b = prevVariants[key];
        if (a?.key !== b?.key || a?.value !== b?.value) {
          return newVariants;
        }
      }
      return prevVariants;
    });
  }, []);

  const handleFetchPromise = useCallback(
    (fetchPromise: Promise<ExperimentClient>) => {
      fetchPromise
        .then(result => {
          updateVariants(result.all());
        })
        .catch(error => logger.error({ error, message: 'Amplitude experiments init failure' }))
        .finally(() => setIsLoading(false));
    },
    [updateVariants]
  );

  useEffect(() => {
    let isMounted = true;

    experimentsCacheSettledPromise
      .then(() => {
        if (isMounted) {
          updateVariants(experiments.all());
        }
      })
      .finally(() => {
        if (isMounted) {
          handleFetchPromise(experimentsFetchPromise);
        }
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logExposureInternal = useCallback(
    (
      flagKey: string | RunningExperiments,
      type: 'Manual' | 'Auto' = 'Manual',
      options?: ExposureOptions
    ) => {
      const foundVariant = variants[flagKey];
      const foundValue = foundVariant?.value;
      const dedupeKey = options?.dedupeKey ?? '';
      const flagAndValueKey = flagKey + foundVariant?.value + dedupeKey;

      if (!flagExposuresHandled.current.has(flagAndValueKey) && foundValue) {
        logEvent('$exposure', EventTypes.Other, {
          flag_key: flagKey,
          variant: foundValue,
        });
        flagExposuresHandled.current.set(flagAndValueKey, true);
        logger.info(
          `[Experiment] ${type} Logging exposure for "${flagKey}", variant value: "${foundVariant.value}"`
        );
      }
    },
    [variants]
  );

  const logExposure = useCallback(
    (flagKey: string | RunningExperiments, options?: ExposureOptions) => {
      logExposureInternal(flagKey, 'Manual', options);
    },
    [logExposureInternal]
  );

  const handleLogin = useCallback(
    (userId: string) => {
      if (userId === lastFetchedUserId) {
        return;
      }
      // this really only runs if a user logs in as lastFetchedUserId is populated at app start
      lastFetchedUserId = userId;
      handleFetchPromise(fetchExperimentsForUser(userId));
    },
    [handleFetchPromise]
  );

  const handleLogout = useCallback(() => {
    experiments.clear();
    flagExposuresHandled.current.clear();
    clearMinimalCartExperimentExposureSessions();
    lastFetchedUserId = undefined;
    handleFetchPromise(fetchExperimentsForUser());
  }, [handleFetchPromise]);

  const getVariantValue = useCallback((flagKey: string | RunningExperiments) => variants[flagKey], [
    variants,
  ]);

  const variant = useCallback(
    (flagKey: string, expectedVariant: string, options = { skipExposureLogging: false }) => {
      if (options.skipExposureLogging !== true) {
        logExposureInternal(flagKey, 'Auto');
      }

      const foundVariant = variants[flagKey];
      return foundVariant?.value === expectedVariant;
    },
    [variants, logExposureInternal]
  );

  const value = useMemoAll({
    isLoading,
    getVariantValue,
    logExposure,
    handleLogin,
    handleLogout,
    allVariants: variants,
    variant,
  });

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
